import { useRef, useState, useCallback, useEffect } from 'react';

import "@esri/calcite-components/components/calcite-shell-panel";

import { APP_CONFIG } from '@config/config';
import { withErrorBoundary } from '@libs/utils/errorBoundaries';
import { useWellSelectionStore } from '@cores/stores/useWellSelectionStore';

import { buildBasemapSource } from './utils/mapUtils';
import { useWellMapCore } from './hooks/useWellMapCore';
import useWellFeatureTable from './hooks/useWellFeatureTable';


import WellMapPanel from './comps/WellMapPanel';
import WellFtTablePanel from './comps/WellFtTablePanel';
import WellDetailsSidebar from './comps/WellDetailsSidebar';
import ResizableSplitPanel from './comps/ResizableSplitPanel';

const GALLERY_SOURCE = buildBasemapSource(
  APP_CONFIG.MAPS.BasemapGallery
);

const FTR_TBL_ID = 'tableDiv';
const LAYER_TITLE = APP_CONFIG.MAPS.Well.title;

const WellArcGISMapper = () => {
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const featuresRef = useRef(null);
  const checkedCount = useWellSelectionStore( (s) => s.checkedRds?.length ?? 0 );

  const [filterChecked, setFilterChecked] =  useState(false);

  // auto-disable if selection shrinks below threshold
  useEffect(() => {
    if (
      checkedCount <=
        APP_CONFIG.NIOGEMS.FILTER_CHECKED_MIN_COUNT &&
      filterChecked
    ) {
      setFilterChecked(false);
    }
  }, [checkedCount, filterChecked]);

  const {
    mapView,
    onarcgisViewReadyChange,
    activateRectangle
  } = useWellMapCore();

  const {
    handleFeatureTableSearch,
    isCheckedMode
  } = useWellFeatureTable({
    mapView,
    layerTitle: LAYER_TITLE,
    ftrTblId: FTR_TBL_ID,
    setFeaturesOpen,
    filterChecked
  });

  const handleMapClick = useCallback(
    (event) => {
      if (!mapView) return;

      setFeaturesOpen(true);

      setTimeout(() => {
        featuresRef.current?.open({
          location: event.detail.mapPoint,
          fetchFeatures: true
        });
      }, 50);
    },
    [mapView]
  );

  return (
    <>
      <calcite-shell-panel
        slot="panel-start"
        position="start"
        display-mode="dock"
        resizable
      >
        <WellDetailsSidebar
          featuresOpen={featuresOpen}
          featuresRef={featuresRef}
          mapView={mapView}
        />
      </calcite-shell-panel>

      <ResizableSplitPanel
        initialMapHeight="60%"
        storageKey="split:mapHeightPct"
      >
        <WellMapPanel
          gallerySource={GALLERY_SOURCE}
          onViewReady={onarcgisViewReadyChange}
          onMapClick={handleMapClick}
          onRectangleClick={activateRectangle}
        />

        <WellFtTablePanel
          tableId={FTR_TBL_ID}
          onApplyFilter={handleFeatureTableSearch}
          checkedCount={checkedCount}
          filterChecked={filterChecked}
          isCheckedMode={isCheckedMode}
          onToggleFilterChecked={setFilterChecked}
        />
      </ResizableSplitPanel>
    </>
  );
};

export default withErrorBoundary(
  WellArcGISMapper,
  {
    componentName: 'WellArcGISMapper',
    resetKeys: ['wells'],
  }
);